import React from 'react';
import { Box, Button, Flex, Spinner, Tabs } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { IconEast, IconWest } from 'shared/icons';

import { DesiredPosition } from './desired-position';
import { WorkExperience } from './work-experience';
import { EducationInfo } from './education-info';
import { AdditionalInfo } from './additonal-info';
import { useCreateResumeContext } from './create-resume.context';
import { CreateResumeMode, CreateResumeStep, isCreateResumeStep } from './create-resume.store';

import './feature-create-resume.css';

const CREATE_RESUME_STEP_QUERY_PARAM = 'step';

const STEP_LABELS: Record<CreateResumeStep, string> = {
   [CreateResumeStep.DESIRED_POSITION]: 'Бажана посада',
   [CreateResumeStep.WORK_EXPERIENCE]: 'Досвід роботи',
   [CreateResumeStep.EDUCATION_INFO]: 'Інформація про освіту',
   [CreateResumeStep.ADDITIONAL_INFO]: 'Додаткова інформація',
};

const SUBMIT_RESUME_MESSAGES: Record<
   CreateResumeMode,
   {
      success: string;
      error: string;
   }
> = {
   [CreateResumeMode.CREATE]: {
      success: 'Резюме збережено ',
      error: 'Не вдалося зберегти резюме',
   },
   [CreateResumeMode.EDIT]: {
      success: 'Резюме оновлено успішно',
      error: 'Не вдалося оновити резюме',
   },
   [CreateResumeMode.COPY]: {
      success: 'Резюме скопійовано успішно',
      error: 'Не вдалося скопіюват резюме',
   },
};

type CreateResumeProps = {
   syncStepWithUrl?: boolean;
};

const CreateResumeStepUrlSync = observer(() => {
   const store = useCreateResumeContext();
   const [searchParams, setSearchParams] = useSearchParams();
   const queryStep = searchParams.get(CREATE_RESUME_STEP_QUERY_PARAM);

   React.useEffect(() => {
      if (!isCreateResumeStep(queryStep)) {
         return;
      }

      if (queryStep !== store.activeStep) {
         store.setActiveStep(queryStep);
      }
   }, [queryStep, store]);

   React.useEffect(() => {
      if (queryStep === store.activeStep) {
         return;
      }

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set(CREATE_RESUME_STEP_QUERY_PARAM, store.activeStep);
      setSearchParams(nextSearchParams, { replace: true });
   }, [queryStep, searchParams, setSearchParams, store.activeStep]);

   return null;
});

const CreateResumeView = ({ syncStepWithUrl = true }: CreateResumeProps) => {
   const store = useCreateResumeContext();
   const navigate = useNavigate();

   const submitResume = () => {
      const submitMessages = SUBMIT_RESUME_MESSAGES[store.mode];

      store.submitResume({
         onSuccess: (response) => {
            toast.success(submitMessages.success);
            navigate('/candidate/my-cv-list');
         },
         onError: (message) => {
            toast.error(submitMessages.error);
         },
      });
   };

   const renderStepActions = () => {
      return (
         <Flex className="create-resume__actions" justify="between" width="100%" gap="3">
            <Flex gap="3">
               <Button
                  type="button"
                  variant="soft"
                  size="3"
                  color="red"
                  disabled={!store.canGoBack}
                  onClick={() => {
                     store.goBack();
                  }}
               >
                  <IconWest /> Попередній крок
               </Button>

               <Button
                  type="button"
                  variant="soft"
                  color="red"
                  size="3"
                  disabled={!store.canGoNext}
                  onClick={() => {
                     store.goNext();
                  }}
               >
                  Наступний крок <IconEast />
               </Button>
            </Flex>

            <Button
               type="button"
               size="3"
               disabled={store.isSubmitting}
               loading={store.isSubmitting}
               onClick={submitResume}
            >
               Зберегти
            </Button>
         </Flex>
      );
   };

  return (
    <Flex className="create-resume" width="100%" direction="column">
      {syncStepWithUrl ? <CreateResumeStepUrlSync /> : null}
      {store.isBootstrapping ? (
            <Flex justify="center" py="8">
               <Spinner size="3" />
            </Flex>
         ) : (
            <Tabs.Root
               value={store.activeStep}
               onValueChange={(value: string) => {
                  store.goToStep(value as CreateResumeStep);
               }}
               className="create-resume__tabs"
            >
               <Tabs.List wrap="wrap" justify="start" className="create-resume__tab-list" size="2">
                  {store.stepOrder.map((step: CreateResumeStep) => (
                     <Tabs.Trigger
                        key={step}
                        className="create-resume__tab-trigger"
                        value={step}
                        disabled={!store.isStepUnlocked(step)}
                     >
                        {STEP_LABELS[step]}
                     </Tabs.Trigger>
                  ))}
               </Tabs.List>

               <Box pt="3" className="create-resume__content">
                  <Tabs.Content value={CreateResumeStep.DESIRED_POSITION}>
                     <DesiredPosition
                        renderSteps={renderStepActions}
                        showErrors={store.shouldShowStepErrors(CreateResumeStep.DESIRED_POSITION)}
                     />
                  </Tabs.Content>

                  <Tabs.Content value={CreateResumeStep.WORK_EXPERIENCE}>
                     <WorkExperience
                        renderSteps={renderStepActions}
                        showErrors={store.shouldShowStepErrors(CreateResumeStep.WORK_EXPERIENCE)}
                     />
                  </Tabs.Content>

                  <Tabs.Content value={CreateResumeStep.EDUCATION_INFO}>
                     <EducationInfo
                        renderSteps={renderStepActions}
                        showErrors={store.shouldShowStepErrors(CreateResumeStep.EDUCATION_INFO)}
                     />
                  </Tabs.Content>

                  <Tabs.Content value={CreateResumeStep.ADDITIONAL_INFO}>
                     <AdditionalInfo
                        renderSteps={renderStepActions}
                        showErrors={store.shouldShowStepErrors(CreateResumeStep.ADDITIONAL_INFO)}
                     />
                  </Tabs.Content>
               </Box>
            </Tabs.Root>
         )}
      </Flex>
   );
};

export const FeatureCreateResume = observer(CreateResumeView);
